import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import { ApiMiddlewarePayload } from '@ocean/api'
import { localization } from '@ocean/configs'
import { AppThunk } from 'store'
import { createApiAction, MiddlewareApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { namespace } from 'modules/virtualClassrooms/common/localization'
import {
  updateVirtualClassroomEvent,
  deleteVirtualClassroomEvent,
  publishVirtualClassroomEvent,
} from 'modules/virtualClassrooms/manager/EventDetails/api/eventDetailsService'
import { reloadEventDetailsData } from 'modules/virtualClassrooms/manager/EventDetails/slices/eventDetails'
import { EventEditorFormParams } from 'modules/virtualClassrooms/manager/EventDetails/types/eventEditor'
import eventListRoutes from 'modules/virtualClassrooms/manager/EventList/routing/routes'
import { EventEditorState, ReloadEventDetailsParams } from './types'

const { i18n } = localization

const I18_PATH = `${namespace}:manager.eventDetails.eventEditor`

const initialState: EventEditorState = {
  eventUpdateRequest: {},
  eventDeleteRequest: {},
  eventPublishRequest: {},
}

const eventEditorSlice = createSlice({
  name: 'virtualClassroomManagerEventDetailsEventEditor',
  initialState,
  reducers: {
    eventUpdateRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.eventUpdateRequest = action.payload
    },
    eventDeleteRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.eventDeleteRequest = action.payload
    },
    eventPublishRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.eventPublishRequest = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { eventUpdateRequested, eventDeleteRequested, eventPublishRequested } =
  eventEditorSlice.actions
export default eventEditorSlice.reducer

const handleReloadEventDetails =
  (response: ApiMiddlewarePayload, params: ReloadEventDetailsParams): AppThunk =>
  async dispatch => {
    const { eventId, payloadAction } = params
    const { error } = response
    if (!error) {
      dispatch(reloadEventDetailsData(eventId) as MiddlewareApiAction)
    }
    dispatch(payloadAction(response))
  }

export const updateEvent = (eventId: Guid, formData: EventEditorFormParams) => {
  return createApiAction({
    apiCalls: {
      updateVirtualClassroomEvent: () => updateVirtualClassroomEvent(eventId, formData),
    },
    payloadAction: eventUpdateRequested,
    nextAction: {
      action: handleReloadEventDetails,
      params: { eventId, payloadAction: eventUpdateRequested },
    },
    enableErrorNotification: true,
    successMessage: i18n.t(`${I18_PATH}.successUpdate`),
    shouldShowGlobalLoading: true,
  })
}

const handleRedirectToEventList =
  (response: ApiMiddlewarePayload): AppThunk =>
  async dispatch => {
    const { error } = response
    if (!error) {
      dispatch(push(eventListRoutes.root))
    }
    dispatch(eventDeleteRequested(response))
  }

export const deleteEvent = (eventId: Guid) => {
  return createApiAction({
    apiCalls: {
      deleteVirtualClassroomEvent: () => deleteVirtualClassroomEvent(eventId),
    },
    payloadAction: eventDeleteRequested,
    nextAction: handleRedirectToEventList,
    enableErrorNotification: true,
    successMessage: i18n.t(`${I18_PATH}.successDelete`),
    shouldShowGlobalLoading: true,
  })
}

export const publishEvent = (eventId: Guid) => {
  return createApiAction({
    apiCalls: {
      publishVirtualClassroomEvent: () => publishVirtualClassroomEvent(eventId),
    },
    payloadAction: eventPublishRequested,
    nextAction: {
      action: handleReloadEventDetails,
      params: { eventId, payloadAction: eventPublishRequested },
    },
    enableErrorNotification: true,
    successMessage: i18n.t(`${I18_PATH}.successPublish`),
  })
}
